import { complianceStore } from '../store/complianceStore';
import type { ComplianceStore } from '../store/complianceStore.types';

/**
 * Initialize CPS compliance policies.
 *
 * This function should be called during app bootstrap to load compliance policies.
 * It uses the vanilla Zustand store so it can be called outside of React context.
 *
 * The function:
 * - Checks if cached policies are still valid
 * - Clears cache if version is out of date
 * - Fetches new policies if needed
 * - Stores policies in localStorage for persistence
 *
 * @param host - The CPS host URL (e.g., https://clients.config.office.net)
 * @param initiator - Optional telemetry initiator identifier (e.g., 'bootstrap_async')
 * @returns Promise that resolves when initialization is complete
 *
 * @example
 * // In bootstrap.ts
 * import { initCompliance } from '@ms/yammer-compliance';
 *
 * // Non-blocking initialization
 * initCompliance(config.msal.resources.ocps, 'bootstrap_async');
 */
export const initCompliance = async (host: string, initiator?: string): Promise<void> => {
  await complianceStore.getState().initialize(host, initiator);
};

/**
 * Clear all compliance data from the store and localStorage.
 *
 * This is useful for logout scenarios or when you need to force a fresh fetch.
 */
export const clearCompliance = (): void => {
  complianceStore.getState().clear();
};

/**
 * Get the current compliance store state synchronously.
 *
 * Useful for non-React contexts where you need to check compliance state.
 */
export const getComplianceState = (): ComplianceStore => complianceStore.getState();
